import { IFieldBaseProps } from "common/components/Field";
import { FormContext } from "common/components/Form/FormContext";
import { useAppTracking } from "common/hooks/storeHooks";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { parsePath } from "common/helpers/FormPath";
import { useTracking } from "react-tracking";

export interface ToggleProps extends IFieldBaseProps<"toggle"> {
  size?: "sm" | "md";
  name: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}

const backgroundSizes = {
  sm: "w-[24px] h-[12.35px]",
  md: "w-[36px] h-[20px]",
};

const circleSizes = {
  sm: "w-[10px] h-[10px] top-[1.175px] bottom-[1.175px] peer-checked:translate-x-[10px]",
  md: "w-[16px] h-[16px] top-[2px] bottom-[2px] peer-checked:translate-x-[16px]",
};

export interface ToggleControlProps extends ToggleProps {
  handleInput: React.ChangeEventHandler<HTMLInputElement>;
  value: boolean;
}

/**
 * Unconnected to redux store. Allows for custom onChange handler and value.
 */
export const ToggleControl: React.FC<Omit<ToggleControlProps, "type">> = ({
  size = "md",
  name,
  label,
  id,
  disabled,
  handleInput,
  value,
}) => {
  const nameId = id || name;
  const bgStyles = value
    ? "bg-secondary-900 peer-hover:bg-secondary-700 peer-focus:ring-secondary-200"
    : "bg-secondary-300 peer-hover:bg-secondary-500 peer-focus:ring-secondary-300";

  return (
    <label
      htmlFor={nameId}
      className={"flex items-center w-fit " + (disabled ? "cursor-not-allowed" : "cursor-pointer")}>
      <div className="relative">
        <input
          type="checkbox"
          id={nameId}
          name={name}
          aria-label={label || name}
          className="hidden peer"
          checked={value}
          onChange={handleInput}
          disabled={disabled}
        />

        {/* Background  */}
        <div
          className={
            bgStyles +
            " " +
            "block rounded-full peer-focus:ring peer-disabled:bg-secondary-100" +
            " " +
            backgroundSizes[size]
          }></div>

        {/* Circle */}
        <div
          className={
            "absolute bg-white rounded-full transition shadow-md left-[2px] right-[2px]" + " " + circleSizes[size]
          }></div>
      </div>

      {label && <div className="ml-3 text-secondary-900 font-medium">{label}</div>}
    </label>
  );
};

export const Toggle: React.FC<ToggleProps> = ({ size = "md", name, label, id, disabled, onChange }) => {
  // Hooks
  const dispatch = useDispatch();
  const { rootPath, updateFormReducer } = React.useContext(FormContext);
  const { trackEvent } = useAppTracking();

  // Constants
  const path = parsePath(name);
  const value = useSelector((state: any) => state.getIn(rootPath.concat(path)));

  // Methods
  const handleInput = (event: any) => {
    const { checked, name } = event.target;
    trackEvent({
      action: "checkBoxChanged",
      category: "CheckBox",
      checkboxType: name,
      type: name,
      value: checked,
      name: label,
    });
    dispatch(updateFormReducer({ rootPath, name, value: checked }));

    if (onChange) onChange(checked);
  };

  return (
    <ToggleControl
      size={size}
      name={name}
      label={label}
      id={id}
      disabled={disabled}
      value={value}
      handleInput={handleInput}
    />
  );
};
